<template>
  <div class="funnel-list-container">
    <div
      v-if="parsedFunnels.length > 0"
      class="funnels-list"
    >
      <!-- Column Headers -->
      <div class="funnel-header">
        <div class="header-name">
          {{ t('templates.funnels.modelName') }}
        </div>
      </div>

      <!-- Funnel Items -->
      <div
        v-for="funnel in parsedFunnels"
        :key="funnel.id"
        class="funnel-item"
      >
        <div class="funnel-content">
          <div class="funnel-name-col">
            <span class="funnel-name">{{ funnel.name }}</span>
            <SpmBadge
              :label="funnel.labelType"
              :icon="getTypeIcon(funnel.type)"
              :colors="getTypeColors(funnel.type)"
            />
          </div>
          <div class="funnel-actions-col">
            <Button
              icon="fa-regular fa-pen-to-square"
              class="p-button-text p-button-secondary p-button-sm edit-btn"
              :label="t('templates.edit')"
              @click="editFunnel(funnel.id, funnel.name, funnel.type)"
            />
          </div>
        </div>
      </div>
    </div>
    <div
      v-else
      class="no-funnels"
    >
      {{ t('templates.funnels.noFunnelsAvailable') }}
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'primevue/button';
import SpmBadge from '@/components/table-rest-api/SpmBadge.vue';
import {
  getChannelIcon as getChannelIconFromTokens,
  getChannelTagStyleVars,
} from '@/views/automated-scenarios/utils/styleTokens';

interface Funnel {
  id: string | number;
  name: string;
  type: string;
}

export default defineComponent({
  name: 'FunnelsListV2',

  components: {
    Button,
    SpmBadge,
  },

  props: {
    templateConfig: {
      type: Object,
      required: true,
    },
  },

  emits: ['edit-funnel'],

  setup(props, { emit }) {
    const { t } = useI18n();

    const parsedFunnels = computed<Funnel[]>(() => {
      try {
        const config = props.templateConfig;
        if (config && config.funnels && Array.isArray(config.funnels)) {
          return config.funnels.map((funnel: Funnel) => ({
            id: funnel.id,
            name: funnel.name,
            type: funnel.type,
            labelType: t(`templates.channels.${funnel.type}`),
          }));
        }
      } catch (e) {
        console.error('Error parsing template_config JSON:', e);
      }
      return [];
    });

    const editFunnel = (templateId: number, name: string, type: string) => {
      emit('edit-funnel', templateId, name, type);
    };

    /**
     * Get type icon using styleTokens
     */
    const getTypeIcon = (type: string): string => getChannelIconFromTokens(type) || 'fa-solid fa-question';

    /**
     * Get type colors for SpmBadge using styleTokens
     */
    const getTypeColors = (type: string): { bg: string; color: string; borderColor?: string } => {
      const vars = getChannelTagStyleVars(type);
      return {
        bg: vars['--channel-bg'] || 'var(--surface-200)',
        color: vars['--channel-color'] || 'var(--text-color)',
        borderColor: vars['--channel-border'],
      };
    };

    return {
      parsedFunnels,
      t,
      editFunnel,
      getTypeIcon,
      getTypeColors,
    };
  },
});
</script>

<style scoped>
.funnel-list-container {
  padding: 0;
}

.funnels-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* Column Headers */
.funnel-header {
  display: flex;
  align-items: center;
  padding: 0.625rem 1.25rem;
  background-color: transparent;
  border-bottom: 1px solid #e5e7eb;
  font-size: 0.8125rem;
  font-weight: 500;
  color: #6b7280;
}

.header-name {
  flex: 1;
  text-align: left;
}

/* Funnel Items */
.funnel-item {
  background: #ffffff;
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 0.15s ease;
}

.funnel-item:last-child {
  border-bottom: none;
}

.funnel-item:hover {
  background-color: #fafbfc;
}

.funnel-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 1.25rem;
}

.funnel-name-col {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  min-width: 0;
}

.funnel-name {
  font-size: 0.875rem;
  font-weight: 400;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 1;
}

.funnel-type-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
  flex-shrink: 0;
}

.funnel-type-badge i {
  font-size: 0.75rem;
}

/* Badge colors matching Templates.vue */
.badge-popup {
  background-color: #fef3c7;
  color: #d97706;
}

.badge-email {
  background-color: #eff6ff;
  color: #1d4ed8;
}

.badge-sms {
  background-color: #f0fdf4;
  color: #15803d;
}

.badge-whatsapp {
  background-color: #ecfdf5;
  color: #059669;
}

.badge-default {
  background-color: #f3f4f6;
  color: #4b5563;
}

.funnel-actions-col {
  flex: 0 0 auto;
  display: flex;
  justify-content: flex-end;
  margin-left: 1rem;
}

.edit-btn {
  border: none !important;
  color: #6b7280 !important;
  padding: 0.4rem 0.875rem !important;
  font-size: 0.8125rem !important;
  font-weight: 400 !important;
  transition: all 0.15s ease !important;
  background-color: transparent !important;
}

.edit-btn:hover {
  color: #374151 !important;
  background-color: #f3f4f6 !important;
}

.edit-btn i {
  margin-right: 0.375rem;
  font-size: 0.875rem;
}

.no-funnels {
  padding: 3rem 1.5rem;
  text-align: center;
  color: #9ca3af;
  font-size: 0.875rem;
  background: transparent;
  border-radius: 8px;
  border: 1px dashed #d1d5db;
}

/* Responsive */
@media (max-width: 768px) {
  .funnel-header {
    display: none;
  }

  .funnel-content {
    grid-template-columns: 1fr;
    gap: 0.875rem;
    padding: 1rem 1.25rem;
  }

  .funnel-name-col,
  .funnel-type-col,
  .funnel-actions-col {
    min-width: 0;
    width: 100%;
  }

  .funnel-type-col {
    justify-content: flex-start;
  }

  .funnel-actions-col {
    justify-content: flex-start;
  }

  .edit-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
